import { Check, Copy, Mail, MessageSquareText } from "lucide-react";
import { useState } from "react";

import type { PlutoOutreachDraft, PlutoOutreachDraftVariant } from "./outreach-draft";
import { Button } from "../ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";

type CopyState = { index: number; status: "copied" | "failed" } | null;

const copyValue = (channel: PlutoOutreachDraft["channel"], variant: PlutoOutreachDraftVariant): string =>
  channel === "email" && variant.subject ? `${variant.subject}\n\n${variant.body}` : variant.body;

type PlutoOutreachDraftCardProps = {
  draft: PlutoOutreachDraft;
};

export const PlutoOutreachDraftCard = ({ draft }: PlutoOutreachDraftCardProps) => {
  const [copyState, setCopyState] = useState<CopyState>(null);

  const copyDraft = async (variant: PlutoOutreachDraftVariant, index: number) => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error("Clipboard API unavailable");
      await navigator.clipboard.writeText(copyValue(draft.channel, variant));
      setCopyState({ index, status: "copied" });
    } catch {
      setCopyState({ index, status: "failed" });
    }
    window.setTimeout(() => setCopyState(null), 1_600);
  };

  const ChannelIcon = draft.channel === "email" ? Mail : MessageSquareText;

  return (
    <article className="max-w-[96%] overflow-hidden rounded-2xl rounded-bl-md border border-stone-300 bg-white shadow-[0_12px_32px_-28px_rgba(28,25,23,0.7)]">
      <header className="border-b border-stone-200 bg-stone-50 px-4 py-3">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-stone-900 text-white">
              <ChannelIcon aria-hidden="true" className="h-3.5 w-3.5" />
            </span>
            <div className="min-w-0">
              <h3 className="truncate text-sm font-semibold text-stone-900">
                {draft.channel === "email" ? "Sales email" : "Sales SMS"} for {draft.companyName}
              </h3>
              <p className="text-[11px] text-stone-500">
                {draft.variants.length > 1
                  ? `Recommended draft + ${String(draft.variants.length - 1)} alternate${
                      draft.variants.length > 2 ? "s" : ""
                    }`
                  : "Recommended draft"}
                {" · Review before sending"}
                {draft.usedCaseSourceCount === 0
                  ? " · No case pages read"
                  : ` · ${String(draft.usedCaseSourceCount)} case page${draft.usedCaseSourceCount > 1 ? "s" : ""} read`}
                {draft.writingInstructionsVersion > 0
                  ? ` · Writing style v${String(draft.writingInstructionsVersion)}`
                  : ""}
              </p>
            </div>
          </div>
        </div>
        <p className="mt-3 border-t border-stone-200 pt-3 text-xs leading-5 text-stone-600">
          <span className="font-medium text-stone-800">Why this angle: </span>
          {draft.rationale}
        </p>
      </header>

      <Tabs className="p-4" defaultValue="variant-0">
        <TabsList
          className="grid w-full bg-stone-100"
          style={{ gridTemplateColumns: `repeat(${String(draft.variants.length)}, minmax(0, 1fr))` }}
        >
          {draft.variants.map((variant, index) => (
            <TabsTrigger className="text-xs" key={index} title={variant.tone} value={`variant-${String(index)}`}>
              <span className="truncate capitalize">{variant.tone}</span>
            </TabsTrigger>
          ))}
        </TabsList>

        {draft.variants.map((variant, index) => {
          const copyStatus = copyState?.index === index ? copyState.status : null;
          return (
            <TabsContent className="mt-4" key={index} value={`variant-${String(index)}`}>
              <div className="rounded-xl border border-stone-200 bg-[#faf9f7] p-4">
                {draft.channel === "email" && variant.subject ? (
                  <div className="mb-3 border-b border-stone-200 pb-3">
                    <span className="text-[11px] font-medium uppercase tracking-wide text-stone-500">Subject</span>
                    <p className="mt-1 text-sm font-medium text-stone-900">{variant.subject}</p>
                  </div>
                ) : null}
                <p className="whitespace-pre-wrap text-sm leading-6 text-stone-700">{variant.body}</p>
              </div>
              <div className="mt-3 flex items-center justify-between gap-3">
                <span aria-live="polite" className="text-xs text-stone-500">
                  {copyStatus === "copied" ? "Copied to clipboard" : copyStatus === "failed" ? "Copy failed" : ""}
                </span>
                <Button
                  aria-label={`Copy the ${variant.tone} draft`}
                  className="h-8 gap-1.5 text-xs"
                  type="button"
                  variant="outline"
                  onClick={() => void copyDraft(variant, index)}
                >
                  {copyStatus === "copied" ? (
                    <Check aria-hidden="true" className="h-3.5 w-3.5" />
                  ) : (
                    <Copy aria-hidden="true" className="h-3.5 w-3.5" />
                  )}
                  {copyStatus === "copied" ? "Copied" : "Copy"}
                </Button>
              </div>
            </TabsContent>
          );
        })}
      </Tabs>
    </article>
  );
};
